<template>
  <Layout>
    <div class="mc-list-header">
      <PageHeader :title="title" :subTitle="subTitle" />

      <div class="clearfix mc-btn-group__list mc-btn-group__list--detail">
        <div class="float-end">
          <McButton
            variant="secondary"
            class="ms-3"
            :text="$t('global.back')"
            icon="icon-outline_arrow-left"
            @click="handleBack"
          />
        </div>
      </div>

      <McTable
        v-show="isTableDataAvailable"
        hasDelete
        withCard
        searchable
        stickyHeader
        hasEmit:deleteErrorV2
        :fields="fields"
        :apiClient="apiClient"
        :customHandlers="{
          handleClickRow: handleClickRow,
          handleAdd: handleAdd,
        }"
        @deleteErrorV2="handleDeleteErrorV2"
        @listRes="handleListResponse"
        @available-change="handleAvailableChange"
      >
        <template #before-table="{ isBusy, isSelected, showDeleteModal }">
          <div class="d-flex gap-2 justify-content-end mb-4">
            <McButton
              variant="secondary"
              icon="icon-outline_upload"
              :text="$t('global.action.upload')"
              @click="$router.push('/master/customer/import')"
            />
            <McButton
              v-if="isSelected"
              variant="secondary-red"
              icon="icon-outline_trash"
              :text="$t('global.delete')"
              @click="showDeleteModal"
            />
            <McButton
              v-if="hasData"
              variant="primary"
              icon="icon-fill_plus"
              :text="$t('global.action.create')"
              :disabled="isBusy"
              @click="handleAdd"
            />
          </div>
        </template>
      </McTable>
      <b-card v-show="!isTableDataAvailable" class="mt-3">
        <div class="empty-table-container">
          <McEmptyState
            :title="$t('maintenance.nolist.noData')"
            :desc="$t('master.service.emptyState.desc')"
            class="mt-3"
            @buttonPrimaryAction="handleAdd"
          />
        </div>
      </b-card>
    </div>
  </Layout>
</template>

<script>
import appConfig from '@/app.config'
import PageHeader from '@/components/page-header-v2'
import Layout from '@/router/layouts/main'
import McTable from '@/components/McTable'
import McButton from '@/components/McButton'
import McEmptyState from '@/components/McEmptyState'
import * as Customer from '@/services/rest-api/lib/customer/customer'
import { Helper } from '@/mixin/helper'
import { mapGetters } from 'vuex'

/**
 * Starter component
 */
export default {
  page: {
    title: 'Master Customer',
    meta: [{ name: 'description', content: appConfig.description }],
  },
  components: {
    Layout,
    PageHeader,
    McTable,
    McButton,
    McEmptyState,
  },
  mixins: [Helper],
  data() {
    return {
      hasData: true,
      isTableDataAvailable: true,
    }
  },
  computed: {
    ...mapGetters({
      getFeatureFlagValue: 'identity/getFeatureFlagValue',
    }),
    title() {
      return this.$t('master.pelanggan')
    },
    subTitle() {
      return this.$t('master.textListPelanggan')
    },
    apiClient() {
      return {
        get: Customer.get,
        show: Customer.show,
        update: Customer.update,
        store: Customer.store,
        delete: Customer.deleteSingle,
      }
    },
    fields() {
      const fields = [
        {
          label: this.$t('master.form.customer.clientName'),
          key: 'name',
          isSortable: true,
          isRedirectable: true,
          isShow: true,
        },
        {
          label: this.$t('master.form.customer.immediateType'),
          key: 'mgm_immediate_type',
          isSortable: true,
          isRedirectable: true,
          isShow: this.hasFeatureFleetTaskContractMGM,
          formatter: (value) => value ? value : '-',
        },
        {
          label: this.$t('master.form.customer.email'),
          key: 'email',
          isSortable: true,
          isShow: true,
        },
        {
          label: this.$t('master.form.customer.industriCategory'),
          key: 'industry_category.name',
          isSortable: true,
          isShow: true,
        },
        {
          label: this.$t('master.form.customer.employeeMarketing'),
          key: 'marketing_user.name',
          isSortable: true,
          isShow: true,
        },
      ]

      return fields.filter(field => field.isShow)
    },
    hasFeatureFleetTaskContractMGM(){
      const data = this.getFeatureFlagValue('customFeature.mgm')
      return Array.isArray(data?.company_id) &&
        data.company_id.includes(this.getCompanyId()) &&
        data?.features?.fleet_task_contract
    }
  },
  methods: {
    handleClickRow(item) {
      this.$router.push({ name: 'Master Customer Update', params: { id: item.id } })
    },
    handleAdd() {
      this.$router.push({ name: 'Master Customer Create' })
    },
    handleBack() {
      this.$router.push({ name: 'Master Home Page' })
    },
    handleListResponse(response) {
      this.hasData = response?.data?.paginated_result?.length > 0
    },
    handleAvailableChange(isAvailable) {
      this.isTableDataAvailable = isAvailable
    },
    handleDeleteErrorV2(data, selected) {
      const dataFailed = data.filter((item) => item !== 'OK')
      const success = selected.length - dataFailed.length
      const failed = dataFailed.length

      if (failed > 0) {
        this.$mcToast.error({
          component: 'McToastDynamicBody',
          props: {
            contents: [
              {
                html: `<strong>${failed}</strong> ${this.$t('master.customer.alert.failedDelete')}`,
              },
            ],
          },
        })
      }

      if (success > 0) {
        this.$mcToast.success({
          component: 'McToastDynamicBody',
          props: {
            contents: [
              {
                html: `<strong>${success}</strong> ${this.$t(
                  'master.customer.alert.successDelete'
                )}`,
              },
            ],
          },
        })
      }
    },
  },
}
</script>
